/**
 * "Remember me" email store for the workplace login flows (DEVOP-5493).
 *
 * Reuses the longstanding `emailForSignIn` localStorage key so the login page
 * can pre-fill the address on the user's next visit. The key is intentionally
 * NOT cleared on logout (see `clearSessionStorage`) so the convenience survives
 * a sign-out — it is only ever written when the user explicitly opts in via the
 * post-sign-in "remember your email?" prompt.
 */
export const EMAIL_FOR_SIGN_IN_KEY = "emailForSignIn";

/**
 * Sentinel stored in `emailForSignIn` when the user picks "Don't ask again on
 * this computer". It's a deliberately non-email value so it can't be confused
 * with a remembered address — `getRememberedEmail` treats it as "nothing
 * remembered", while `isRememberEmailPromptSuppressed` recognizes it. Reusing
 * the same key keeps the whole feature on one localStorage entry that already
 * survives logout (see `clearSessionStorage`), so the opt-out is permanent.
 */
export const PROMPT_SUPPRESSED_VALUE = "__do-not-ask__";

/**
 * Window event dispatched when a sign-in flow requests the remember-email
 * prompt. The root-level `RememberEmailPromptManager` listens for it and shows
 * the dialog after the post-sign-in redirect, so the prompt never blocks the
 * redirect or depends on the (short-lived) auth page staying mounted.
 */
export const REMEMBER_EMAIL_PROMPT_EVENT = "cbh:remember-email-prompt";

/** sessionStorage key holding the email awaiting a remember-me decision. */
const PENDING_PROMPT_KEY = "rememberEmailPromptPending";

function safeLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Accessing localStorage can throw in some privacy modes / sandboxed iframes.
    return undefined;
  }
}

function safeSessionStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

/** The email remembered on this device, if any (never the suppression sentinel). */
export function getRememberedEmail(): string | undefined {
  const value = safeLocalStorage()?.getItem(EMAIL_FOR_SIGN_IN_KEY) ?? undefined;
  return value && value !== PROMPT_SUPPRESSED_VALUE ? value : undefined;
}

/** True when `email` is already the remembered address (case-insensitive). */
export function isEmailRemembered(email: string): boolean {
  const remembered = getRememberedEmail();
  if (!remembered) {
    return false;
  }
  return remembered.trim().toLowerCase() === email.trim().toLowerCase();
}

/** Persist `email` so the login page pre-fills it next time. */
export function rememberEmail(email: string): void {
  const trimmed = email.trim();
  if (!trimmed) {
    return;
  }
  safeLocalStorage()?.setItem(EMAIL_FOR_SIGN_IN_KEY, trimmed);
}

/**
 * Forget any remembered email. Called when the user declines the prompt so a
 * previously-remembered address (e.g. a different user on a shared computer)
 * isn't left behind.
 */
export function forgetRememberedEmail(): void {
  safeLocalStorage()?.removeItem(EMAIL_FOR_SIGN_IN_KEY);
}

/** Record an email awaiting a remember-me decision (consumed by the manager). */
export function setPendingRememberEmailPrompt(email: string): void {
  const trimmed = email.trim();
  if (!trimmed) {
    return;
  }
  safeSessionStorage()?.setItem(PENDING_PROMPT_KEY, trimmed);
}

/** The email awaiting a remember-me decision, if any. */
export function getPendingRememberEmailPrompt(): string | undefined {
  return safeSessionStorage()?.getItem(PENDING_PROMPT_KEY) ?? undefined;
}

export function clearPendingRememberEmailPrompt(): void {
  safeSessionStorage()?.removeItem(PENDING_PROMPT_KEY);
}

/** True when the user has permanently dismissed the prompt on this device. */
export function isRememberEmailPromptSuppressed(): boolean {
  return safeLocalStorage()?.getItem(EMAIL_FOR_SIGN_IN_KEY) === PROMPT_SUPPRESSED_VALUE;
}

/** Permanently suppress the prompt on this device ("Don't ask again"). */
export function suppressRememberEmailPrompt(): void {
  safeLocalStorage()?.setItem(EMAIL_FOR_SIGN_IN_KEY, PROMPT_SUPPRESSED_VALUE);
}
